// Copyright © 2019-2026 Focal Systems. All rights reserved.

import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";

/**
 * A bar docked to the bottom of the page, below the scrolling content rather than inside it —
 * so a form's Save/Discard stays in reach however far down the form you've scrolled.
 */
const PageFooterContext = createContext<{
  slot: HTMLElement | null;
  setSlot: (el: HTMLElement | null) => void;
  height: number;
} | null>(null);

export function PageFooterProvider({ children }: { children: ReactNode }) {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const [height, setHeight] = useState(0);

  useEffect(
    function trackDockedFooterHeight() {
      if (!slot) return;
      const observer = new ResizeObserver(() => setHeight(slot.getBoundingClientRect().height));
      observer.observe(slot);
      return () => observer.disconnect();
    },
    [slot],
  );

  return <PageFooterContext.Provider value={{ slot, setSlot, height }}>{children}</PageFooterContext.Provider>;
}

/** Height of the docked footer in px; 0 when nothing is docked, so floating UI can sit above it. */
export function usePageFooterHeight(): number {
  return useContext(PageFooterContext)?.height ?? 0;
}

/** Where the shell mounts docked footers — a sibling after the scroll area, so it never scrolls away. */
export function PageFooterSlot() {
  const ctx = useContext(PageFooterContext);
  return <div ref={ctx?.setSlot} className="shrink-0 empty:hidden" />;
}

/**
 * Renders `children` into the docked footer. Outside the shell (tests, a page rendered bare) there's
 * no slot, so it renders in place instead of vanishing.
 *
 * The portal moves the DOM out of any enclosing `<form>`, so a submit button inside must name its
 * form via the `form` attribute.
 */
export function PageFooter({ children }: { children: ReactNode }) {
  const ctx = useContext(PageFooterContext);
  if (!ctx) return children;
  if (!ctx.slot) return null;
  return createPortal(<div className="border-t bg-muted/50 px-6 py-3">{children}</div>, ctx.slot);
}
